import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IUser } from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { storeAppUsers } from "src/redux/actions/Structure.Actions";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";

/**
 * Custom hook to fetch and store application users in the Redux store, and return a map of users by their ID.
 * It checks if the users are already present in the store before making an API call to fetch them.
 * The returned map allows for efficient access to user data by their ID.
 *
 * @returns {Map<string, IUser>} A map of user IDs to user objects.
 */
export function useAppUsersById(): Map<string, IUser> {
  const dispatch = useDispatch();
  const appUsers = useSelector((state: IRootState) => state.structures.appUsers);

  useEffect(() => {
    const fetchUsers = async (): Promise<void> => {
      try {
        const response = await AmplifyAPIWrapper.GetPaginate<IUser>("/users");
        if (response) dispatch(storeAppUsers(response));
      } catch (error) {
        console.error("useAppUsersById: failed to fetch /users", error);
      }
    };
    if (!appUsers?.length) void fetchUsers();
  }, [appUsers, dispatch]);

  return useMemo(() => {
    const map = new Map<string, IUser>();
    (appUsers ?? []).forEach((user) => {
      if (user?._id) map.set(user._id, user);
    });
    return map;
  }, [appUsers]);
}
